import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Card, Table, Button, Badge, Spinner, Modal, Form, Tabs, Tab } from "react-bootstrap";
import Select from "react-select"; 
import axios from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// --- CHILD COMPONENT: LOG ROW ---
const LogRow = ({ log, isGlobalEditing, onUpdate, onDelete, setGlobalEditingId }) => {
  const logId = log.id !== undefined ? log.id : log.Id;
  const [isLocalEditing, setIsLocalEditing] = useState(false);
  const [localEditData, setLocalEditData] = useState({ createdAt: "", workLocation: "" });

  const isForgot = (
    (log.deviceNote || "").toLowerCase().includes("forgot") || 
    (log.workLocation || "").toLowerCase().includes("forgot")
  );

  const handleEditInit = () => {
    const d = new Date(log.createdAt || log.CreatedAt);
    const localDateTime = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setLocalEditData({ createdAt: localDateTime, workLocation: log.workLocation || "" });
    setIsLocalEditing(true);
    setGlobalEditingId(logId);
  };

  const handleCancel = () => {
    setIsLocalEditing(false);
    setGlobalEditingId(null);
  };

  const handleSave = async () => {
    if (logId === undefined) return;
    const success = await onUpdate(logId, localEditData);
    if (success) {
      setIsLocalEditing(false);
      setGlobalEditingId(null);
    }
  };

  return (
    <tr className={isForgot ? "table-danger-custom" : ""}>
      <td className="ps-3 align-middle text-center">
        {isLocalEditing ? (
          <Form.Control type="datetime-local" size="sm" value={localEditData.createdAt} 
            onChange={e => setLocalEditData({...localEditData, createdAt: e.target.value})} />
        ) : (
          <span className={`fw-bold ${isForgot ? "text-danger" : ""}`}>
            {new Date(log.createdAt || log.CreatedAt).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'})}
          </span>
        )}
      </td>
      <td className="align-middle text-center">
        <Badge bg={log.actionType === "LOGIN" ? "primary" : "secondary"} className="rounded-pill px-3 shadow-sm">
          {log.actionType}
        </Badge>
      </td>
      <td className={`fw-bold align-middle text-center ${isForgot ? "text-danger" : "text-success"}`}>
        {(log.durationHours || log.DurationHours || 0).toFixed(2)}h
      </td>
      <td className="align-middle">
        {isLocalEditing ? (
          <Form.Control type="text" size="sm" value={localEditData.workLocation} 
            onChange={e => setLocalEditData({...localEditData, workLocation: e.target.value})} />
        ) : (
          <div className="d-flex align-items-center">
            <small className={`fw-medium ${isForgot ? "text-danger" : "text-dark"}`}>{log.workLocation || "N/A"}</small>
            {log.deviceNote && (
              <Badge bg="danger" className="ms-2 shadow-sm" style={{fontSize: '0.65rem'}}>
                {log.deviceNote}
              </Badge>
            )}
          </div>
        )}
      </td>
      <td className="text-center align-middle">
        {isLocalEditing ? (
          <div className="d-flex gap-1 justify-content-center">
            <Button variant="success" size="sm" className="rounded-circle shadow-sm" onClick={handleSave}><i className="fas fa-check"></i></Button>
            <Button variant="light" size="sm" className="rounded-circle shadow-sm border" onClick={handleCancel}><i className="fas fa-times"></i></Button>
          </div>
        ) : (
          <div className="d-flex gap-1 justify-content-center">
            <Button variant="outline-primary" size="sm" className="btn-tool" disabled={isGlobalEditing} onClick={handleEditInit}><i className="fas fa-edit"></i></Button>
            <Button variant="outline-danger" size="sm" className="btn-tool" disabled={isGlobalEditing} onClick={() => onDelete(logId)}><i className="fas fa-trash-alt"></i></Button>
          </div>
        )}
      </td>
    </tr>
  );
};

// --- ATTENDANCE LEGEND ---
const AttendanceLegend = () => (
    <div className="p-3 bg-light rounded-3 border d-flex flex-wrap gap-3 align-items-center mb-3">
        <span className="fw-bold text-dark small me-2"><i className="fas fa-info-circle me-1"></i> LEAVE TYPES:</span>
        <div className="d-flex align-items-center gap-1"><span className="badge-leave">P</span><small className="fw-medium text-muted">Paid Annual Leave</small></div>
        <div className="d-flex align-items-center gap-1"><span className="badge-leave">TS</span><small className="fw-medium text-muted">Maternity Leave</small></div>
        <div className="d-flex align-items-center gap-1"><span className="badge-leave">KL</span><small className="fw-medium text-muted">Unpaid Leave</small></div>
        <div className="d-flex align-items-center gap-1"><span className="badge-leave">Ô/S</span><small className="fw-medium text-muted">Sick Leave</small></div>
        <div className="d-flex align-items-center gap-1"><span className="badge-leave">C</span><small className="fw-medium text-muted">Wedding Leave</small></div>
    </div>
);

// --- MAIN COMPONENT ---
const AttendanceMatrix = () => {
  const [rawData, setRawData] = useState({ attendance: [], leaves: [] });
  const [users, setUsers] = useState([]);
  const [otData, setOtData] = useState({}); 
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(null); 
  const [showModal, setShowModal] = useState(false);
  const [expandedOtUserId, setExpandedOtUserId] = useState(null); 
  const [selectedDetail, setSelectedDetail] = useState({ employeeName: "", employeeId: null, date: "", logs: [] });
  const [editingLogId, setEditingLogId] = useState(null);
  const [activeTab, setActiveTab] = useState("matrix");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLogData, setNewLogData] = useState({ actionType: "LOGIN", createdAt: "", workLocation: "Office" });

  const getInitialDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return { start: formatDate(firstDay), end: formatDate(lastDay) };
  };

  const defaultDates = getInitialDates();
  const [filters, setFilters] = useState({ startDate: defaultDates.start, endDate: defaultDates.end, selectedUser: null });

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(0, i).toLocaleString('en-US', { month: 'long' }) }));
  const yearOptions = Array.from({ length: 5 }, (_, i) => ({ value: new Date().getFullYear() - i, label: (new Date().getFullYear() - i).toString() }));

  const selectStyles = { control: (base) => ({ ...base, borderRadius: "20px", fontSize: '0.85rem' }), menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

  const handleQuickMonthYear = (type, val) => {
    if (!val) return;
    const currentStartDate = new Date(filters.startDate);
    const m = type === 'month' ? val.value : (currentStartDate.getMonth() + 1);
    const y = type === 'year' ? val.value : currentStartDate.getFullYear();
    const start = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const end = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    setFilters({ ...filters, startDate: start, endDate: end });
  };

  const fetchOTForUsers = async (userList, startDate) => {
    const dateObj = new Date(startDate);
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();
    const otMap = {};
    
    const requests = userList.map(async (u) => {
      try {
        const res = await axios.get(`${API_ENDPOINTS.otRequests || (API_ENDPOINTS.attendance + "/../OTRequests")}/Stats/TotalHours/${u.id}`, { 
            params: { month, year } 
        });
        otMap[u.id] = {
            total: res.data.totalHours || 0,
            details: res.data.details || [] 
        };
      } catch (e) { 
        otMap[u.id] = { total: 0, details: [] }; 
      }
    });

    await Promise.all(requests);
    setOtData(otMap);
  };

  const fetchData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const res = await axios.get(`${API_ENDPOINTS.attendance}/logs`, {
        params: { startDate: filters.startDate, endDate: filters.endDate, userId: filters.selectedUser?.value || "" }
      });
      setRawData(res.data);
      
      if (users.length > 0) {
        fetchOTForUsers(users, filters.startDate);
      }

      if (showModal) {
        const attendanceList = res.data.attendance || [];
        const updated = attendanceList.filter(log => 
          (log.employeeName || log.EmployeeName) === selectedDetail.employeeName &&
          new Date(log.createdAt || log.CreatedAt).toLocaleDateString('en-GB') === selectedDetail.date
        );
        setSelectedDetail(prev => ({ ...prev, logs: updated }));
      }
    } catch (err) { console.error("Fetch Error:", err); }
    finally { setLoading(false); }
  }, [filters, showModal, selectedDetail.employeeName, selectedDetail.date, users]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(API_ENDPOINTS.users);
        const userList = res.data || [];
        setUsers(userList);
        if (userList.length > 0) {
            fetchOTForUsers(userList, filters.startDate);
        }
      } catch (e) { console.error(e); }
    };
    init();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const daysInRange = useMemo(() => {
    const start = new Date(filters.startDate); const end = new Date(filters.endDate);
    const days = []; let curr = new Date(start);
    if (isNaN(curr.getTime())) return [];
    while (curr <= end) { days.push(new Date(curr)); curr.setDate(curr.getDate() + 1); }
    return days;
  }, [filters.startDate, filters.endDate]);

  const matrixData = useMemo(() => {
    const grouped = {};
    const filteredUsers = filters.selectedUser ? users.filter(u => u.id === filters.selectedUser.value) : users;

    filteredUsers.forEach(u => {
      const name = u.fullName || u.userName;
      grouped[name] = { 
        name, userId: u.id, days: {}, totalHours: 0, meritValue: 0, 
        fullMeritDays: 0, incompleteDays: 0, incompleteDatesList: [],
        otBase: otData[u.id]?.total || 0,
        otDetails: otData[u.id]?.details || []
      };
    });

    (rawData.attendance || []).forEach(log => {
      const name = log.employeeName || log.EmployeeName || "N/A";
      if (grouped[name]) {
        const dateObj = new Date(log.createdAt || log.CreatedAt);
        const dateStr = dateObj.toDateString();
        if (!grouped[name].days[dateStr]) {
            grouped[name].days[dateStr] = { hours: 0, warn: false, logs: [], dayOfWeek: dateObj.getDay() };
        }
        const h = log.durationHours || log.DurationHours || 0;
        grouped[name].days[dateStr].hours += h;
        grouped[name].totalHours += h;
        grouped[name].days[dateStr].logs.push(log);
        if (((log.deviceNote || "") + (log.workLocation || "")).toLowerCase().includes("forgot")) grouped[name].days[dateStr].warn = true;
      }
    });

    (rawData.leaves || []).forEach(leave => {
        const user = users.find(u => u.id.toString() === leave.userId.toString());
        if (!user) return;
        const name = user.fullName || user.userName;
        if (!grouped[name]) return;
        let curr = new Date(leave.startDate);
        const end = new Date(leave.endDate);
        while (curr <= end) {
            const dateStr = curr.toDateString();
            if (!grouped[name].days[dateStr]) grouped[name].days[dateStr] = { hours: 0, warn: false, logs: [], dayOfWeek: curr.getDay(), leaveSymbol: leave.symbol };
            else grouped[name].days[dateStr].leaveSymbol = leave.symbol;
            curr.setDate(curr.getDate() + 1);
        }
    });

    Object.values(grouped).forEach(u => {
        daysInRange.forEach(d => {
            const dateStr = d.toDateString();
            const dayOfWeek = d.getDay();
            const dayData = u.days[dateStr] || { hours: 0, leaveSymbol: null };
            if (!dayData.leaveSymbol) {
                if (dayOfWeek === 6) { 
                    if (dayData.hours >= 4) { u.meritValue += 0.5; u.fullMeritDays += 1; }
                    else { u.incompleteDays += 1; u.incompleteDatesList.push(d.toLocaleDateString('en-GB', {day:'2-digit', month:'2-digit'})); }
                } else if (dayOfWeek !== 0) { 
                    if (dayData.hours >= 8) { u.meritValue += 1; u.fullMeritDays += 1; }
                    else { u.incompleteDays += 1; u.incompleteDatesList.push(d.toLocaleDateString('en-GB', {day:'2-digit', month:'2-digit'})); }
                }
            }
        });
    });

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [rawData, users, filters.selectedUser, daysInRange, otData]);

  // --- EXPORT EXCEL FUNCTION ---
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    
    // SHEET 1: MATRIX VIEW
    const sheet1 = workbook.addWorksheet("Attendance Matrix");
    const headerRow1 = ["#", "Staff Name", ...daysInRange.map(d => `${d.getDate()}/${d.getMonth()+1}`), "Total"];
    const worksheet1Header = sheet1.addRow(headerRow1);
    
    worksheet1Header.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4F81BD' } };
        cell.alignment = { horizontal: 'center' };
    });

    matrixData.forEach((u, idx) => {
        const rowValues = [
            idx + 1,
            u.name,
            ...daysInRange.map(d => {
                const day = u.days[d.toDateString()];
                return day?.leaveSymbol || (day ? day.hours.toFixed(1) : "");
            }),
            u.totalHours.toFixed(1)
        ];
        const row = sheet1.addRow(rowValues);
        // Highlight logic for leave or warning in Excel
        daysInRange.forEach((d, i) => {
            const day = u.days[d.toDateString()];
            if (day?.leaveSymbol) {
                row.getCell(i + 3).font = { bold: true, color: { argb: '8B0000' } };
            }
        });
    });

    // SHEET 2: MERIT CALCULATION
    const sheet2 = workbook.addWorksheet("Merit & OT");
    const headerRow2 = ["#", "Staff Name", "Incomplete Days", "Working Hours", "OT (x1.5)", "Grand Total", "Merit Value"];
    const worksheet2Header = sheet2.addRow(headerRow2);
    
    worksheet2Header.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'C0504D' } };
        cell.alignment = { horizontal: 'center' };
    });

    matrixData.forEach((u, idx) => {
        const otCalculated = u.otBase * 1.5;
        const grandTotal = u.totalHours + otCalculated;
        sheet2.addRow([
            idx + 1,
            u.name,
            `${u.incompleteDays} days (${u.incompleteDatesList.join(", ")})`,
            u.totalHours.toFixed(1),
            otCalculated.toFixed(2),
            grandTotal.toFixed(2),
            u.meritValue
        ]);
    });

    // Finalize
    sheet1.columns.forEach(col => col.width = 15);
    sheet2.columns.forEach(col => col.width = 20);

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Attendance_Summary_${filters.startDate}_to_${filters.endDate}.xlsx`);
  };

  const handleUpdateLog = async (id, data) => {
    try {
      await axios.put(`${API_ENDPOINTS.attendance}/log/${id}`, data);
      fetchData(true);
      return true;
    } catch (err) { return false; }
  };

  const handleDeleteLog = async (id) => {
    if (window.confirm("Delete this log?")) {
      try {
        await axios.delete(`${API_ENDPOINTS.attendance}/log/${id}`);
        fetchData(true);
      } catch (err) { console.error(err); }
    }
  };

  const handleAddLog = async () => {
    const payload = { userId: String(selectedDetail.employeeId), actionType: newLogData.actionType, createdAt: newLogData.createdAt, workLocation: newLogData.workLocation };
    try {
      await axios.post(`${API_ENDPOINTS.attendance}/log`, payload);
      setShowAddForm(false);
      fetchData(true);
    } catch (err) { alert("Failed to add log"); }
  };

  const handleSendEmailRequest = async (u) => {
    setEmailLoading(u.userId);
    const otCalculated = u.otBase * 1.5;
    const grandTotal = u.totalHours + otCalculated;

    try {
      await axios.post(`${API_ENDPOINTS.attendance}/SendAttendanceSummaryEmail`, {
        userId: u.userId, 
        monthYear: `${filters.startDate} to ${filters.endDate}`,
        totalMerit: u.meritValue, 
        fullMeritDays: u.fullMeritDays,
        incompleteDays: u.incompleteDays, 
        incompleteDates: u.incompleteDatesList,
        workingHours: u.totalHours,
        otHours: otCalculated,
        otBase: u.otBase,
        grandTotal: grandTotal
      });
      alert(`Summary sent successfully to ${u.name}!`);
    } catch (err) { alert("Failed to send email summary."); }
    finally { setEmailLoading(null); }
  };

  const openDetailModal = (userName, dateStr, logs, userId) => {
    const [d, m, y] = dateStr.split('/');
    const dateObj = new Date(`${y}-${m}-${d}T08:00`);
    const localDateTime = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setNewLogData({ ...newLogData, createdAt: localDateTime, actionType: "LOGIN" });
    setSelectedDetail({ employeeName: userName, date: dateStr, logs: logs || [], employeeId: userId });
    setShowModal(true);
  };

  return (
    <div className="attendance-matrix-wrapper">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold text-primary m-0">ATTENDANCE MONITOR</h4>
          <p className="text-muted small mb-0">Enterprise Management System</p>
        </div>
        <div className="d-flex gap-2">
            <Button variant="outline-success" className="rounded-pill px-4 shadow-sm" onClick={exportToExcel}>
                <i className="fas fa-file-excel me-2"></i>EXPORT EXCEL
            </Button>
            <Button variant="primary" className="rounded-pill px-4 shadow" onClick={() => fetchData()}>REFRESH</Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm mb-4 p-3 bg-white rounded-4">
        <Row className="g-3 align-items-end mb-3">
          <Col md={2}><label className="filter-label">QUICK MONTH</label><Select options={monthOptions} placeholder="Month..." isClearable menuPortalTarget={document.body} onChange={(v) => handleQuickMonthYear('month', v)} styles={selectStyles} /></Col>
          <Col md={1}><label className="filter-label">YEAR</label><Select options={yearOptions} placeholder="Year" menuPortalTarget={document.body} onChange={(v) => handleQuickMonthYear('year', v)} styles={selectStyles} /></Col>
          <Col md={2}><label className="filter-label">FROM</label><Form.Control type="date" className="rounded-pill" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} /></Col>
          <Col md={2}><label className="filter-label">TO</label><Form.Control type="date" className="rounded-pill" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} /></Col>
          <Col md={3}><label className="filter-label">STAFF SEARCH</label><Select isClearable options={users.map(u => ({value: u.id, label: u.fullName || u.userName}))} placeholder="Search staff..." menuPortalTarget={document.body} onChange={opt => setFilters({...filters, selectedUser: opt})} styles={selectStyles} /></Col>
        </Row>
        <AttendanceLegend />
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3 custom-tabs">
        <Tab eventKey="matrix" title={<span><i className="fas fa-th me-2"></i>Matrix View</span>}>
          <Card className="table-card border-0 shadow-lg bg-white rounded-4 overflow-hidden">
            <div className="table-responsive custom-scroll">
              <Table hover bordered={false} className="m-0 align-middle">
                <thead className="header-white">
                  <tr>
                    <th className="sticky-col stt-header">#</th>
                    <th className="sticky-col name-header">STAFF NAME</th>
                    {daysInRange.map((d, i) => (
                      <th key={i} className="day-header text-center"><span className="d-block day-num">{d.getDate()}</span><small className="day-name">{d.toLocaleDateString('en-US',{weekday:'short'})}</small></th>
                    ))}
                    <th className="total-header text-center">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? <tr><td colSpan={100} className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr> : 
                    matrixData.map((u, idx) => (
                    <tr key={idx}>
                      <td className="sticky-col stt-cell text-center">{idx + 1}</td>
                      <td className="sticky-col name-cell fw-bold text-primary">{u.name}</td>
                      {daysInRange.map((d, i) => {
                        const day = u.days[d.toDateString()];
                        const isLeave = !!day?.leaveSymbol;
                        return (
                          <td key={i} className={`matrix-cell ${day?.warn ? "forgot-highlight" : isLeave ? "leave-highlight" : day ? "active-cell" : "empty-cell"}`}
                              onClick={() => openDetailModal(u.name, d.toLocaleDateString('en-GB'), day?.logs, u.userId)}>
                            {isLeave ? <span className="leave-text-bold">{day.leaveSymbol}</span> : (day ? day.hours.toFixed(1) : "")}
                          </td>
                        );
                      })}
                      <td className="text-center fw-bold text-danger total-cell">{u.totalHours.toFixed(1)}h</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        </Tab>
        
        <Tab eventKey="merit" title={<span><i className="fas fa-medal me-2"></i>Merit Calculation</span>}>
          <Card className="border-0 shadow-lg bg-white rounded-4 p-4">
             <Table hover responsive className="align-middle border-0">
                <thead className="bg-light">
                   <tr>
                      <th className="text-center">#</th>
                      <th>STAFF NAME</th>
                      <th className="text-center">INCOMPLETE</th>
                      <th className="text-center">WORKING HOURS</th>
                      <th className="text-center">OT (x1.5)</th>
                      <th className="text-center">GRAND TOTAL</th>
                      <th className="text-center">MERIT VALUE</th>
                      <th className="text-center">ACTION</th>
                   </tr>
                </thead>
                <tbody>
                   {matrixData.map((u, idx) => {
                      const otCalculated = u.otBase * 1.5;
                      const grandTotal = u.totalHours + otCalculated;
                      const isExpanded = expandedOtUserId === u.userId;
                      return (
                        <React.Fragment key={u.userId}>
                            <tr className={isExpanded ? "table-active" : ""}>
                            <td className="text-center">{idx + 1}</td>
                            <td className="fw-bold">{u.name}</td>
                            <td className="text-center">
                                <span className="text-danger fw-bold">{u.incompleteDays} days</span>
                                <div className="d-flex flex-wrap gap-1 justify-content-center mt-1">
                                    {u.incompleteDatesList.slice(0, 3).map((dt, i) => <Badge key={i} bg="light" text="dark" className="border" style={{fontSize: '0.6rem'}}>{dt}</Badge>)}
                                    {u.incompleteDatesList.length > 3 && <small className="text-muted">+{u.incompleteDatesList.length-3}</small>}
                                </div>
                            </td>
                            <td className="text-center fw-bold text-muted">{u.totalHours.toFixed(1)}h</td>
                            <td className="text-center">
                                <div className={`d-flex flex-column align-items-center ot-badge-click ${isExpanded ? "active" : ""}`} 
                                     onClick={() => setExpandedOtUserId(isExpanded ? null : u.userId)}>
                                    <span className="fw-bold text-orange">{otCalculated.toFixed(2)}h</span>
                                    <small className="text-muted" style={{fontSize: '0.7rem'}}>Base: {u.otBase}h <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} ms-1`}></i></small>
                                </div>
                            </td>
                            <td className="text-center fw-bold text-primary" style={{fontSize: '1.1rem'}}>{grandTotal.toFixed(2)}h</td>
                            <td className="text-center"><Badge bg="success" className="px-3 rounded-pill">{u.meritValue} Merit</Badge></td>
                            <td className="text-center">
                                <Button variant="outline-primary" size="sm" className="rounded-pill" disabled={emailLoading === u.userId} onClick={() => handleSendEmailRequest(u)}>
                                    {emailLoading === u.userId ? <Spinner animation="border" size="sm" /> : <><i className="fas fa-paper-plane me-2"></i>SEND</>}
                                </Button>
                            </td>
                            </tr>
                            {isExpanded && (
                                <tr>
                                    <td colSpan={8} className="p-0 border-0 bg-white">
                                        <div className="ot-detail-expand shadow-inner p-3 mx-3 mb-3 rounded-3 border">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <small className="fw-bold text-muted text-uppercase"><i className="fas fa-clock me-2"></i>Chi tiết giờ tăng ca</small>
                                                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setExpandedOtUserId(null)}>Đóng</Button>
                                            </div>
                                            <Table size="sm" className="mb-0 small border rounded">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="text-center">Ngày</th>
                                                        <th className="text-center">Giờ (Base)</th>
                                                        <th>Lý do / Ghi chú</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {u.otDetails && u.otDetails.length > 0 ? u.otDetails.map((detail, dIdx) => (
                                                        <tr key={dIdx}>
                                                            <td className="text-center fw-bold text-muted">{new Date(detail.date).toLocaleDateString('en-GB')}</td>
                                                            <td className="text-center text-orange fw-bold">{detail.hours.toFixed(2)}h</td>
                                                            <td className="text-muted">{detail.reason || "N/A"}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr><td colSpan={3} className="text-center py-2 italic text-muted">Không có dữ liệu chi tiết.</td></tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                      )
                   })}
                </tbody>
             </Table>
          </Card>
        </Tab>
      </Tabs>

      {/* --- MODAL LOG CHI TIẾT --- */}
      <Modal show={showModal} onHide={() => { setShowModal(false); setEditingLogId(null); setShowAddForm(false); }} centered size="xl" className="custom-modal">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">LOG DETAILS: {selectedDetail.employeeName} ({selectedDetail.date})</Modal.Title>
          <Button variant="success" size="sm" className="ms-auto me-3 rounded-pill" onClick={() => setShowAddForm(!showAddForm)}>{showAddForm ? "CANCEL" : "ADD LOG"}</Button>
        </Modal.Header>
        <Modal.Body className="p-0">
          {showAddForm && (
            <div className="bg-light p-3 border-bottom shadow-sm">
               <Row className="g-2">
                  <Col md={3}><Form.Select size="sm" value={newLogData.actionType} onChange={e => setNewLogData({...newLogData, actionType: e.target.value})}><option value="LOGIN">LOGIN</option><option value="LOGOUT">LOGOUT</option></Form.Select></Col>
                  <Col md={3}><Form.Control size="sm" type="datetime-local" value={newLogData.createdAt} onChange={e => setNewLogData({...newLogData, createdAt: e.target.value})} /></Col>
                  <Col md={4}><Form.Control size="sm" placeholder="Location" value={newLogData.workLocation} onChange={e => setNewLogData({...newLogData, workLocation: e.target.value})} /></Col>
                  <Col md={2}><Button variant="primary" size="sm" className="w-100" onClick={handleAddLog}>SUBMIT</Button></Col>
               </Row>
            </div>
          )}
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr><th className="text-center py-3">TIME</th><th className="text-center">STATUS</th><th className="text-center">DURATION</th><th>LOCATION</th><th className="text-center">TOOLS</th></tr>
            </thead>
            <tbody>
              {selectedDetail.logs.map((l) => (
                <LogRow key={l.id || l.Id} log={l} isGlobalEditing={editingLogId !== null && editingLogId !== (l.id || l.Id)} onUpdate={handleUpdateLog} onDelete={handleDeleteLog} setGlobalEditingId={setEditingLogId} />
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      <style>{`
        .attendance-matrix-wrapper { background-color: #f1f5f9; min-height: 100vh; padding: 2rem; font-family: 'Inter', sans-serif; }
        .header-white th { background-color: #fff !important; color: #334155 !important; border-bottom: 2px solid #f1f5f9 !important; padding: 15px 5px; text-transform: uppercase; font-size: 0.8rem; font-weight: 800; }
        .sticky-col { position: sticky; left: 0; z-index: 10; background: white !important; }
        .stt-header { min-width: 50px; left: 0; }
        .name-header { min-width: 200px; left: 50px; border-right: 1px solid #f1f5f9 !important; }
        .stt-cell { left: 0; }
        .name-cell { left: 50px; border-right: 4px solid #007bff !important; }
        .matrix-cell { text-align: center; cursor: pointer; font-size: 0.8rem; min-width: 50px; height: 45px; vertical-align: middle; }
        .active-cell { background-color: #f0f7ff; color: #007bff; font-weight: 700; }
        .forgot-highlight { background-color: #fff1f2 !important; color: #e11d48 !important; }
        .leave-highlight { background-color: #fff5f5 !important; }
        .leave-text-bold { font-weight: 900; color: #8B0000; }
        .text-orange { color: #f97316; }
        .badge-leave { background-color: #8B0000; color: white; border-radius: 4px; padding: 2px 6px; font-weight: bold; font-size: 0.7rem; }
        .filter-label { font-size: 0.65rem; font-weight: 800; color: #475569; margin-bottom: 4px; }
        .ot-badge-click { cursor: pointer; padding: 4px 8px; border-radius: 8px; transition: all 0.2s; }
        .ot-badge-click:hover { background-color: #fff7ed; border: 1px inset #ffedd5; }
        .ot-badge-click.active { background-color: #ffedd5; border: 1px solid #fdba74; }
        .ot-detail-expand { animation: slideDown 0.3s ease-out; background-color: #fcfcfc; }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AttendanceMatrix;